"use client"

import { Fragment, useEffect, useMemo, useState, useCallback } from "react"
import { toast } from "sonner"
import * as XLSX from "xlsx"
import {
  Search,
  Download,
  RefreshCw,
  Baby,
  Users,
  Filter,
  X,
  Phone,
  GraduationCap,
  MapPin,
  FileText,
  ChevronDown,
  Sparkles,
  TrendingUp,
  Flame,
  Zap,
  Snowflake,
  Ban,
} from "@/lib/icons"

import { ProtectedRoute } from "@/components/protected-route"
import { Sidebar } from "@/components/sidebar"
import { useAuth } from "@/context/auth-context"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { getLeads, supabase } from "@/lib/supabase"
import { LEAD_STAGE_META, normalizeLeadStage } from "@/lib/lead-stages"
import { LeadSourceBadge, getSourceTooltip, SOURCE_META } from "@/components/kanban/lead-source-badge"
import { NIVEL_LABELS, NIVEL_OPTIONS as NIVEL_OPTIONS_BASE } from "@/lib/educational-levels"

// Paleta de colores por nivel — visual coherente con el resto del CRM
const NIVEL_META = {
  maternal: { label: "Maternal", color: "#f59e0b", bg: "bg-amber-100 dark:bg-amber-950/40", text: "text-amber-700 dark:text-amber-300", ring: "ring-amber-300/50" },
  kinder: { label: "Kinder", color: "#f59e0b", bg: "bg-amber-100 dark:bg-amber-950/40", text: "text-amber-700 dark:text-amber-300", ring: "ring-amber-300/50" },
  prefirst: { label: "Pre-First", color: "#f59e0b", bg: "bg-amber-100 dark:bg-amber-950/40", text: "text-amber-700 dark:text-amber-300", ring: "ring-amber-300/50" },
  primaria: { label: "Primaria", color: "#22c55e", bg: "bg-emerald-100 dark:bg-emerald-950/40", text: "text-emerald-700 dark:text-emerald-300", ring: "ring-emerald-300/50" },
  secundaria: { label: "Secundaria", color: "#0ea5e9", bg: "bg-sky-100 dark:bg-sky-950/40", text: "text-sky-700 dark:text-sky-300", ring: "ring-sky-300/50" },
  preparatoria: { label: "Prepa", color: "#a855f7", bg: "bg-violet-100 dark:bg-violet-950/40", text: "text-violet-700 dark:text-violet-300", ring: "ring-violet-300/50" },
}

const SEXO_AVATAR = {
  nino: { bg: "bg-sky-100 dark:bg-sky-950/40", text: "text-sky-700 dark:text-sky-300", ring: "ring-sky-200 dark:ring-sky-900/50", symbol: "♂" },
  nina: { bg: "bg-pink-100 dark:bg-pink-950/40", text: "text-pink-700 dark:text-pink-300", ring: "ring-pink-200 dark:ring-pink-900/50", symbol: "♀" },
  default: { bg: "bg-rose-100 dark:bg-rose-950/40", text: "text-rose-700 dark:text-rose-300", ring: "ring-rose-200 dark:ring-rose-900/50", symbol: "" },
}

// Estilos para badge de calidad del lead — coherente con lead-card.jsx
const CALIDAD_META = {
  caliente: { label: "Caliente", icon: Flame, bg: "bg-red-50 dark:bg-red-950/40", text: "text-red-700 dark:text-red-300", ring: "ring-red-300/50" },
  tibio: { label: "Tibio", icon: Zap, bg: "bg-amber-50 dark:bg-amber-950/40", text: "text-amber-700 dark:text-amber-300", ring: "ring-amber-300/50" },
  frio: { label: "Frío", icon: Snowflake, bg: "bg-blue-50 dark:bg-blue-950/40", text: "text-blue-700 dark:text-blue-300", ring: "ring-blue-300/50" },
  spam: { label: "Spam", icon: Ban, bg: "bg-slate-100 dark:bg-slate-800/60", text: "text-slate-500 dark:text-slate-400", ring: "ring-slate-300/50" },
}

function getInitials(name) {
  if (!name) return "?"
  return name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()
}

const ESTADO_OPTIONS = [
  { value: "", label: "Todos los estados" },
  { value: "nuevo", label: "Nuevo" },
  { value: "contactado", label: "Contactado" },
  { value: "cita_solicitada", label: "Cita solicitada" },
  { value: "cita", label: "Cita" },
  { value: "interesado", label: "Entrevista / Examen" },
  { value: "entrega_resultado", label: "Entrega resultado" },
  { value: "pendiente_pago", label: "Pendiente de pago" },
  { value: "inscrito", label: "Inscrito" },
  { value: "perdido", label: "Perdido" },
]

const NIVEL_OPTIONS = [
  { value: "", label: "Todos los niveles" },
  ...NIVEL_OPTIONS_BASE,
]

function formatDate(iso) {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleDateString("es-MX", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
  } catch {
    return "—"
  }
}

function formatDateOnly(iso) {
  if (!iso) return ""
  try {
    const d = new Date(iso)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
  } catch {
    return ""
  }
}

export default function LeadsTablePage() {
  const { user } = useAuth()
  const [leads, setLeads] = useState([])
  const [childrenByLead, setChildrenByLead] = useState({})
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [filterEstado, setFilterEstado] = useState("")
  const [filterNivel, setFilterNivel] = useState("")
  const [filterDia, setFilterDia] = useState("all") // today | week | all
  const [filterSource, setFilterSource] = useState("")
  const [mostrarOperativos, setMostrarOperativos] = useState(false) // por default ocultos
  const [expandedId, setExpandedId] = useState(null)

  const load = useCallback(async () => {
    if (!user?.email) return
    setLoading(true)
    try {
      const { data } = await getLeads(user.email, { limit: 2000 })
      setLeads(data || [])
      const leadIds = (data || []).map((l) => l.id).filter(Boolean)
      if (leadIds.length) {
        const { data: kids, error } = await supabase
          .from("lead_children")
          .select("*")
          .in("lead_id", leadIds)
          .order("orden", { ascending: true })
        if (!error && kids) {
          const grouped = {}
          for (const k of kids) {
            if (!grouped[k.lead_id]) grouped[k.lead_id] = []
            grouped[k.lead_id].push(k)
          }
          setChildrenByLead(grouped)
        }
      } else {
        setChildrenByLead({})
      }
    } catch (err) {
      toast.error("Error al cargar prospectos")
    } finally {
      setLoading(false)
    }
  }, [user?.email])

  useEffect(() => {
    load()
  }, [load])

  const filtered = useMemo(() => {
    const now = new Date()
    const todayStr = formatDateOnly(now.toISOString())
    const weekAgo = new Date(now)
    weekAgo.setDate(weekAgo.getDate() - 7)

    const term = search.trim().toLowerCase()

    return leads.filter((l) => {
      // Ocultar operativos por default (padres inscritos con asuntos no-admisión)
      if (!mostrarOperativos && l.es_operativo) return false
      if (filterEstado && normalizeLeadStage(l.estado_lead) !== filterEstado) return false
      if (filterNivel && (l.nivel_educativo || "").toLowerCase() !== filterNivel) return false
      if (filterSource) {
        const leadSource = String(l.source || "").toLowerCase().trim()
        if (filterSource === "sin_fuente") {
          if (leadSource) return false
        } else if (leadSource !== filterSource) {
          return false
        }
      }
      if (filterDia === "today") {
        if (formatDateOnly(l.created_at) !== todayStr) return false
      } else if (filterDia === "week") {
        if (new Date(l.created_at) < weekAgo) return false
      }
      if (term) {
        const hay = `${l.nombre_tutor || ""} ${l.nombre_alumno || ""} ${l.telefono || ""} ${l.email || ""}`.toLowerCase()
        if (!hay.includes(term)) return false
      }
      return true
    })
  }, [leads, search, filterEstado, filterNivel, filterDia, filterSource, mostrarOperativos])

  const stats = useMemo(() => {
    const totalHijos = filtered.reduce((sum, l) => {
      const kids = childrenByLead[l.id]
      if (kids?.length) return sum + kids.length
      return sum + Math.max(l.num_hijos || 1, 1)
    }, 0)
    const familias = filtered.filter((l) => {
      const kids = childrenByLead[l.id]
      return (kids?.length || l.num_hijos || 0) >= 2
    }).length

    // Contador por fuente
    const bySource = {}
    for (const l of filtered) {
      const key = l.source || "sin_fuente"
      bySource[key] = (bySource[key] || 0) + 1
    }

    return { totalLeads: filtered.length, totalHijos, familias, bySource }
  }, [filtered, childrenByLead])

  const handleExport = () => {
    if (!filtered.length) {
      toast.error("No hay prospectos para exportar")
      return
    }
    const hoja1 = filtered.map((l) => ({
      "Fecha registro": formatDate(l.created_at),
      "Tutor": l.nombre_tutor || "",
      "Alumno": l.nombre_alumno || "",
      "Teléfono": l.telefono || "",
      "Email": l.email || "",
      "Nivel": NIVEL_LABELS[(l.nivel_educativo || "").toLowerCase()] || l.nivel_educativo || "",
      "Grado": l.grado_especifico || "",
      "Estado del lead": LEAD_STAGE_META[normalizeLeadStage(l.estado_lead)]?.title || l.estado_lead || "",
      "Campus": l.campus || "",
      "Asesor": l.asesor_nombre || "",
      "# Hijos": childrenByLead[l.id]?.length || l.num_hijos || 1,
      "Calidad": l.calidad_lead || "",
      "Tema consulta": l.tema_consulta || "",
      "Fecha cita": formatDate(l.fecha_cita),
      "Notas": l.notas || "",
    }))

    const hoja2 = []
    for (const l of filtered) {
      const kids = childrenByLead[l.id] || []
      for (const k of kids) {
        hoja2.push({
          "Tutor": l.nombre_tutor || "",
          "Teléfono tutor": l.telefono || "",
          "Nombre del hijo": k.nombre || "",
          "Nivel": NIVEL_LABELS[(k.nivel_educativo || "").toLowerCase()] || k.nivel_educativo || "",
          "Grado": k.grado_especifico || "",
          "Edad": k.edad ?? "",
          "Notas": k.notas || "",
        })
      }
    }

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hoja1), "Prospectos")
    if (hoja2.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hoja2), "Hijos")
    }
    const fecha = formatDateOnly(new Date().toISOString())
    XLSX.writeFile(wb, `prospectos_tepeyac_${fecha}.xlsx`)
    toast.success("Archivo Excel descargado")
  }

  const clearFilters = () => {
    setSearch("")
    setFilterEstado("")
    setFilterNivel("")
    setFilterDia("all")
    setFilterSource("")
  }

  const activeFiltersCount =
    (search ? 1 : 0) + (filterEstado ? 1 : 0) + (filterNivel ? 1 : 0) + (filterDia !== "all" ? 1 : 0) + (filterSource ? 1 : 0)

  return (
    <ProtectedRoute>
      <Sidebar>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
          {/* Header */}
          <div className="sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur">
            <div className="px-4 sm:px-6 py-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-rose-700" />
                    Base de prospectos
                  </h1>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Vista estilo Excel con todos los datos y familias
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs"
                    onClick={load}
                    disabled={loading}
                  >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                    Actualizar
                  </Button>
                  <Button
                    size="sm"
                    className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={handleExport}
                    disabled={!filtered.length}
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    Exportar Excel
                  </Button>
                </div>
              </div>

              {/* Stats — pills horizontales compactas */}
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {[
                  {
                    label: "Prospectos",
                    value: stats.totalLeads,
                    icon: Users,
                    color: "#7a1b33",
                  },
                  {
                    label: "Familias",
                    value: stats.familias,
                    icon: Sparkles,
                    color: "#ec4899",
                    suffix: stats.totalLeads > 0
                      ? `(${Math.round((stats.familias / Math.max(stats.totalLeads, 1)) * 100)}%)`
                      : null,
                  },
                  {
                    label: "Niños",
                    value: stats.totalHijos,
                    icon: Baby,
                    color: "#10b981",
                    suffix: stats.totalLeads > 0
                      ? `· ${(stats.totalHijos / Math.max(stats.totalLeads, 1)).toFixed(1)} por familia`
                      : null,
                  },
                ].map((s, i) => {
                  const Icon = s.icon
                  return (
                    <div
                      key={i}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/70 dark:border-slate-700/60 bg-white dark:bg-slate-900 pl-1.5 pr-3 py-1 shadow-sm hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                    >
                      <span
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full flex-shrink-0"
                        style={{ backgroundColor: `${s.color}18`, color: s.color }}
                      >
                        <Icon className="h-3 w-3" />
                      </span>
                      <span className="text-[12px] font-extrabold tabular-nums text-slate-900 dark:text-slate-100 leading-none">
                        {s.value}
                      </span>
                      <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 leading-none">
                        {s.label}
                      </span>
                      {s.suffix && (
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium tabular-nums leading-none">
                          {s.suffix}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Breakdown por fuente — clickable filter */}
              {stats.totalLeads > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <span className="text-[9px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 mr-1">
                    Por origen:
                  </span>
                  {Object.entries(stats.bySource)
                    .sort((a, b) => b[1] - a[1])
                    .map(([source, count]) => {
                      if (source === "sin_fuente") {
                        const isActive = filterSource === "sin_fuente"
                        return (
                          <button
                            key={source}
                            type="button"
                            onClick={() => setFilterSource(isActive ? "" : "sin_fuente")}
                            title={isActive ? "Quitar filtro" : "Mostrar solo leads sin fuente"}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold transition-all ${
                              isActive
                                ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-md scale-105"
                                : "bg-slate-100/70 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                            }`}
                          >
                            <span className={isActive ? "text-white/70 dark:text-slate-600" : "text-slate-400"}>∅</span>
                            <span>Sin fuente</span>
                            <span className={`px-1 rounded-full ${isActive ? "bg-white/20" : "bg-white dark:bg-slate-900"}`}>{count}</span>
                          </button>
                        )
                      }
                      const meta = SOURCE_META[source]
                      if (!meta) return null
                      const isActive = filterSource === source
                      return (
                        <button
                          key={source}
                          type="button"
                          onClick={() => setFilterSource(isActive ? "" : source)}
                          title={`Filtrar por ${meta.label}`}
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold transition-all ${
                            isActive
                              ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-md scale-105"
                              : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500 hover:shadow-sm"
                          }`}
                        >
                          <LeadSourceBadge source={source} size={18} ringed={false} />
                          <span>{meta.label}</span>
                          <span className={`px-1 rounded-full ${isActive ? "bg-white/20" : "bg-slate-100 dark:bg-slate-800"}`}>
                            {count}
                          </span>
                        </button>
                      )
                    })}
                </div>
              )}

              {/* Filters */}
              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre, teléfono, email..."
                    className="h-9 text-xs pl-9"
                  />
                </div>
                <select
                  value={filterDia}
                  onChange={(e) => setFilterDia(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-xs"
                >
                  <option value="today">Hoy</option>
                  <option value="week">Últimos 7 días</option>
                  <option value="all">Todos</option>
                </select>
                <select
                  value={filterEstado}
                  onChange={(e) => setFilterEstado(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-xs"
                >
                  {ESTADO_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <select
                  value={filterNivel}
                  onChange={(e) => setFilterNivel(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-xs"
                >
                  {NIVEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <select
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-xs"
                  title="Filtrar por origen"
                >
                  <option value="">Todas las fuentes</option>
                  {Object.entries(SOURCE_META)
                    .filter(([key]) => !["facebook", "instagram", "whatsapp"].includes(key))
                    .map(([key, meta]) => (
                      <option key={key} value={key}>{meta.label}</option>
                    ))}
                  <option value="sin_fuente">Sin fuente</option>
                </select>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 text-xs text-slate-500"
                    onClick={clearFilters}
                  >
                    <X className="h-3 w-3 mr-1" /> Limpiar
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="px-4 sm:px-6 py-4">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800">
                    <tr className="text-left text-[10px] uppercase font-semibold text-slate-500">
                      <th className="px-3 py-2.5 w-8"></th>
                      <th className="px-3 py-2.5">Prospecto</th>
                      <th className="px-3 py-2.5">Contacto</th>
                      <th className="px-3 py-2.5">Calidad</th>
                      <th className="px-3 py-2.5">Etapa</th>
                      <th className="px-3 py-2.5">Origen</th>
                      <th className="px-3 py-2.5">Nivel</th>
                      <th className="px-3 py-2.5 text-center"># Hijos</th>
                      <th className="px-3 py-2.5">Asesor</th>
                      <th className="px-3 py-2.5">Fecha cita</th>
                      <th className="px-3 py-2.5">Creado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={11} className="px-3 py-8 text-center text-slate-400">
                          <RefreshCw className="h-4 w-4 animate-spin inline mr-2" />
                          Cargando...
                        </td>
                      </tr>
                    )}
                    {!loading && filtered.length === 0 && (
                      <tr>
                        <td colSpan={11} className="px-3 py-16 text-center">
                          <div className="flex flex-col items-center">
                            <div className="h-14 w-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                              <FileText className="h-7 w-7 text-slate-300 dark:text-slate-600" />
                            </div>
                            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                              Sin prospectos
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              No hay resultados con los filtros actuales. Prueba a limpiar filtros.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {!loading && filtered.map((l) => {
                      const stage = normalizeLeadStage(l.estado_lead)
                      const stageTitle = LEAD_STAGE_META[stage]?.title || l.estado_lead || "—"
                      const stageAccent = LEAD_STAGE_META[stage]?.accent || "#94a3b8"
                      const kids = childrenByLead[l.id] || []
                      const numHijos = kids.length || l.num_hijos || 1
                      const isFamily = numHijos >= 2
                      const isExpanded = expandedId === l.id
                      const nivelKey = (l.nivel_educativo || "").toLowerCase()
                      const nivelMeta = NIVEL_META[nivelKey]
                      return (
                        <Fragment key={l.id}>
                          <tr
                            className={`border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors cursor-pointer ${
                              isFamily ? "bg-rose-50/30 dark:bg-rose-950/10" : ""
                            } ${isExpanded ? "bg-rose-50/60 dark:bg-rose-950/20" : ""}`}
                            onClick={() => setExpandedId(isExpanded ? null : l.id)}
                          >
                            {/* Indicador expandible */}
                            <td className="pl-3 pr-1 py-2.5 w-8">
                              {isFamily && kids.length > 0 ? (
                                <ChevronDown
                                  className={`h-3.5 w-3.5 text-rose-500 transition-transform ${isExpanded ? "rotate-0" : "-rotate-90"}`}
                                />
                              ) : null}
                            </td>

                            {/* Prospecto: avatar + nombre + email */}
                            <td className="px-2 py-2.5">
                              <div className="flex items-center gap-2.5">
                                <div
                                  className={`flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-bold flex-shrink-0 shadow-sm ${
                                    isFamily
                                      ? "bg-gradient-to-br from-rose-500 to-pink-600 text-white ring-2 ring-rose-200 dark:ring-rose-900/40"
                                      : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                                  }`}
                                >
                                  {getInitials(l.nombre_tutor)}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[12.5px] font-bold text-slate-900 dark:text-slate-100 truncate">
                                    {l.nombre_tutor || "Sin nombre"}
                                  </p>
                                  {l.email && (
                                    <p className="text-[10px] text-slate-400 truncate">{l.email}</p>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Contacto: teléfono + alumno */}
                            <td className="px-2 py-2.5">
                              <div className="min-w-0">
                                <p className="text-[11.5px] text-slate-700 dark:text-slate-300 font-semibold tabular-nums">
                                  {l.telefono || "—"}
                                </p>
                                {l.nombre_alumno && (
                                  <p className="text-[10px] text-slate-400 truncate">
                                    Alumno: {l.nombre_alumno}
                                  </p>
                                )}
                              </div>
                            </td>

                            {/* Calidad */}
                            <td className="px-2 py-2.5">
                              {l.calidad_lead && CALIDAD_META[l.calidad_lead] ? (() => {
                                const cal = CALIDAD_META[l.calidad_lead]
                                const CalIcon = cal.icon
                                return (
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold ring-1 ${cal.bg} ${cal.text} ${cal.ring}`}
                                  >
                                    <CalIcon className="h-2.5 w-2.5" />
                                    {cal.label}
                                  </span>
                                )
                              })() : (
                                <span className="text-[10px] text-slate-300">—</span>
                              )}
                            </td>

                            {/* Etapa con punto de color */}
                            <td className="px-2 py-2.5">
                              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">
                                <span
                                  className="inline-block h-2 w-2 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: stageAccent }}
                                />
                                {stageTitle}
                              </span>
                            </td>

                            {/* Origen desplegado */}
                            <td className="px-2 py-2.5">
                              {l.source ? (
                                <LeadSourceBadge
                                  source={l.source}
                                  sourceDetail={l.source_detail}
                                  expanded
                                />
                              ) : (
                                <span className="text-[10px] text-slate-300">—</span>
                              )}
                            </td>

                            {/* Nivel con color */}
                            <td className="px-2 py-2.5">
                              {nivelMeta ? (
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ring-1 ${nivelMeta.bg} ${nivelMeta.text} ${nivelMeta.ring}`}
                                >
                                  <GraduationCap className="h-2.5 w-2.5" />
                                  {nivelMeta.label}
                                </span>
                              ) : (
                                <span className="text-[10px] text-slate-400">—</span>
                              )}
                            </td>

                            {/* # Hijos */}
                            <td className="px-2 py-2.5 text-center">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ring-1 ${
                                  isFamily
                                    ? "bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 ring-rose-300/60 shadow-sm"
                                    : "bg-slate-100 dark:bg-slate-800 text-slate-500 ring-slate-200/60"
                                }`}
                              >
                                <Baby className="h-2.5 w-2.5" />
                                {numHijos}
                              </span>
                            </td>

                            {/* Asesor */}
                            <td className="px-2 py-2.5 text-[11px] text-slate-600 dark:text-slate-400">
                              {l.asesor_nombre || "—"}
                            </td>

                            {/* Fecha cita */}
                            <td className="px-2 py-2.5 text-[10.5px] text-slate-600 dark:text-slate-400 whitespace-nowrap">
                              {formatDate(l.fecha_cita)}
                            </td>

                            {/* Creado */}
                            <td className="pl-2 pr-3 py-2.5 text-[10.5px] text-slate-500 tabular-nums whitespace-nowrap">
                              {formatDate(l.created_at)}
                            </td>
                          </tr>

                          {/* Fila expandida con hijos */}
                          {isExpanded && kids.length > 0 && (
                            <tr className="bg-gradient-to-b from-rose-50/80 to-rose-50/30 dark:from-rose-950/20 dark:to-rose-950/5 border-b border-rose-200/60 dark:border-rose-900/30">
                              <td colSpan={11} className="px-4 sm:px-6 py-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="h-6 w-6 rounded-lg bg-rose-100 dark:bg-rose-950/40 flex items-center justify-center">
                                    <Users className="h-3 w-3 text-rose-700 dark:text-rose-300" />
                                  </div>
                                  <p className="text-[11px] uppercase font-bold tracking-wider text-rose-700 dark:text-rose-300">
                                    Hijos registrados
                                  </p>
                                  <span className="text-[10px] font-bold bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 px-2 py-0.5 rounded-full ring-1 ring-rose-300/40">
                                    {kids.length}
                                  </span>
                                </div>
                                <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                  {kids.map((k) => {
                                    const sexoMeta = SEXO_AVATAR[k.sexo] || SEXO_AVATAR.default
                                    const kNivelKey = (k.nivel_educativo || "").toLowerCase()
                                    const kNivelMeta = NIVEL_META[kNivelKey]
                                    return (
                                      <div
                                        key={k.id}
                                        className="group relative rounded-xl border border-rose-200/70 dark:border-rose-900/40 bg-white dark:bg-slate-900 p-3 hover:shadow-md hover:border-rose-300 dark:hover:border-rose-800 transition-all"
                                      >
                                        <div className="flex items-start gap-2.5">
                                          <div
                                            className={`flex h-9 w-9 items-center justify-center rounded-full ${sexoMeta.bg} ${sexoMeta.text} font-bold text-sm flex-shrink-0 ring-2 ${sexoMeta.ring}`}
                                          >
                                            {(k.nombre || "?").charAt(0).toUpperCase()}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-[12px] font-bold text-slate-900 dark:text-slate-100 truncate flex items-center gap-1">
                                              {k.nombre}
                                              {sexoMeta.symbol && (
                                                <span className={`text-[10px] ${sexoMeta.text}`}>{sexoMeta.symbol}</span>
                                              )}
                                            </p>
                                            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                              {kNivelMeta && (
                                                <span
                                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold ${kNivelMeta.bg} ${kNivelMeta.text}`}
                                                >
                                                  {kNivelMeta.label}
                                                </span>
                                              )}
                                              {k.grado_especifico && (
                                                <span className="text-[9.5px] text-slate-500 dark:text-slate-400">
                                                  {k.grado_especifico}
                                                </span>
                                              )}
                                              {k.edad != null && (
                                                <span className="text-[9.5px] text-slate-400">· {k.edad} años</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </Sidebar>
    </ProtectedRoute>
  )
}
